/// <reference path="../../public/assets/cogi-ui/cogi-widget.d.ts" />
import { useEffect, useState, useRef, useCallback } from "react";

const THEME_COLORS = {
    passion: { primary: '#F50000', glow: '#F50000', bubble: '#F50000' },
    clarity: { primary: '#1d4ed8', glow: '#2563eb', bubble: '#1d4ed8' },
    happy: { primary: '#F59E0B', glow: '#FBBF24', bubble: '#F59E0B' },
    neutral: { primary: '#8B5CF6', glow: '#A78BFA', bubble: '#8B5CF6' },
    yawn: { primary: '#84905E', glow: '#A3AD7C', bubble: '#84905E' },
    surprise: { primary: '#FF5A36', glow: '#FF7A59', bubble: '#FF5A36' },
    sad: { primary: '#5B6C9A', glow: '#7F92BF', bubble: '#5B6C9A' },
    focused: { primary: '#06B6D4', glow: '#22D3EE', bubble: '#06B6D4' },
};

function getThemeColors() {
    const theme = document.documentElement.getAttribute('data-theme') || 'passion';
    return THEME_COLORS[theme] || THEME_COLORS.passion;
}

const WELCOME_MESSAGES = [
    "Welcome \u2014 I'm Cogi, powered by the Emotional Intelligence Engine. Ask me anything about how EIE transforms the way businesses connect with people.",
    "90% of decisions happen through emotion, not logic. I'm Cogi \u2014 ask me how EIE turns that truth into your competitive advantage.",
    "I'm Cogi, your EIE-powered assistant. Ask me how emotional intelligence is reshaping industries \u2014 from media to healthcare.",
    "Hey \u2014 I'm Cogi by Alpha Cogs. I can show you what happens when AI truly understands human emotion. What would you like to know?",
];

const PLACEHOLDERS = [
    "Ask me anything about EIE or Alpha Cogs!",
    "How can emotional intelligence help your business?",
    "Curious about EIE? Just ask...",
    "What industry are you in? I'll show you what EIE can do.",
];

const COGI_CDN_BASE = import.meta.env.VITE_COGI_CDN_BASE || 'https://cogi.alphahub.fan/ui/latest';
const COGI_CSS_URL = `${COGI_CDN_BASE}/cogi-widget.css`;
const COGI_JS_URL = `${COGI_CDN_BASE}/cogi-widget.iife.js`;
const COGI_LOCAL_CSS_URL = '/assets/cogi-ui/cogi-widget.css';
const COGI_LOCAL_JS_URL = '/assets/cogi-ui/cogi-widget.js';
const IS_PROD_BUILD = import.meta.env.PROD;

function Chat() {
    const [view, setView] = useState("closed");
    const cogiInstance = useRef(null);
    const scriptLoaded = useRef(false);

    const syncLiveWidgetConfigColors = useCallback((colors) => {
        const configService = cogiInstance.current?.core?.container?.resolve?.('config');
        if (!configService || typeof configService.set !== 'function') return;

        configService.set('primaryColor', colors.primary);
        configService.set('headerColor', colors.primary);
        configService.set('assistantBubbleColor', colors.bubble);
        configService.set('glowColor', colors.glow);

        if (cogiInstance.current?.config) {
            cogiInstance.current.config.primaryColor = colors.primary;
            cogiInstance.current.config.headerColor = colors.primary;
            cogiInstance.current.config.assistantBubbleColor = colors.bubble;
            cogiInstance.current.config.glowColor = colors.glow;
        }
    }, []);

    const applyThemeToLiveWidget = useCallback((colors) => {
        const container = document.getElementById('cogi-widget-container');
        if (!container) return false;

        syncLiveWidgetConfigColors(colors);

        container.style.setProperty('--cogi-primary', colors.primary);
        container.style.setProperty('--cogi-glow', colors.glow);
        container.style.setProperty('--cogi-assistant-bubble', colors.bubble);

        // Some widget nodes are authored with inline styles (and even inline !important),
        // so update them directly to avoid stuck colors.
        const toggleBtn = container.querySelector('.cogi-toggle-btn');
        if (toggleBtn) {
            toggleBtn.style.setProperty('--glow-color', colors.glow);
            toggleBtn.style.setProperty('background-color', colors.primary);
        }

        const header = container.querySelector('.cogi-header');
        if (header) {
            header.style.setProperty('background-color', colors.primary, 'important');
        }

        const sendBtn = container.querySelector('.cogi-send-btn');
        if (sendBtn) {
            sendBtn.style.setProperty('background-color', colors.primary);
        }

        const assistantBubbles = container.querySelectorAll('.cogi-message-assistant .cogi-message-content');
        assistantBubbles.forEach((el) => {
            el.style.setProperty('background-color', colors.bubble);
            el.style.setProperty('transition', 'background-color 520ms cubic-bezier(0.22, 1, 0.36, 1), color 520ms cubic-bezier(0.22, 1, 0.36, 1)');
        });

        return true;
    }, [syncLiveWidgetConfigColors]);

    const applyThemeWithRetries = useCallback((colors) => {
        if (applyThemeToLiveWidget(colors)) return;
        window.setTimeout(() => applyThemeToLiveWidget(colors), 40);
        window.setTimeout(() => applyThemeToLiveWidget(colors), 160);
        window.setTimeout(() => applyThemeToLiveWidget(colors), 400);
    }, [applyThemeToLiveWidget]);

    const initWidget = useCallback(async () => {
        if (!window.Cogi) return;
        const webhookUrl = import.meta.env.VITE_WEBHOOK_URL;
        const colors = getThemeColors();

        // If widget already exists, live-update colors instead of destroy/re-init to avoid flicker.
        if (cogiInstance.current) {
            applyThemeWithRetries(colors);
            return;
        }

        const config = {
            webhookUrl,
            position: 'bottom-right',
            apiKey: import.meta.env.VITE_COGI_API_KEY,
            primaryColor: colors.primary,
            glowColor: colors.glow,
            assistantBubbleColor: colors.bubble,
            secondaryColor: '#f8f9fa',
            buttonBorderWidth: '3px',
            buttonBorderColor: '#f8f9fa',
            assistantTextColor: '#ffffff',
            userBubbleColor: '#ffffff',
            userTextColor: '#333333',
            enableUserDataRequest: true,
            enableSiteHelp: true,
            placeholder: PLACEHOLDERS[Math.floor(Math.random() * PLACEHOLDERS.length)],
            welcomeMessage: WELCOME_MESSAGES[Math.floor(Math.random() * WELCOME_MESSAGES.length)],
            width: '60vw',
            height: '80vh',
            title: 'Cogi',
            subtitle: 'Alpha Cogs Partner Assistant',
            streaming: true,
            fontFamily: 'Montserrat, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif',
            customIcon: '/assets/img/ai_chatbot_avatar.jpg',
            enableTextToSpeech: true,
            enableHandsFreeMode: true,
            // Premium TTS OpenAI voice
            ttsVoice: 'fable', // puoi cambiare in 'echo', 'fable', 'nova', 'onyx', 'shimmer'
            premiumTts: true, // <--- Ensure premium TTS is enabled
            voiceRate: 1,
            voicePitch: 1,
            voiceGender: 'male',
            // This widget build resolves voice as: voiceName || (gender-based default),
            // so keep voiceName aligned with the desired premium TTS voice.
            voiceName: 'fable',
            onOpen: () => setView('chat'),
            onClose: () => setView('closed')
        };

        try {
            window.Cogi.init(config);
            cogiInstance.current = window.Cogi.instance;
            if (cogiInstance.current && cogiInstance.current.show) {
                await cogiInstance.current.show();
            }
            applyThemeWithRetries(colors);
        } catch (e) {
            console.error('Failed to init Cogi widget', e);
        }
    }, [applyThemeWithRetries]);

    useEffect(() => {
        if (!document.querySelector('link[href*="cogi-widget.css"]')) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = IS_PROD_BUILD ? COGI_CSS_URL : COGI_LOCAL_CSS_URL;
            link.onerror = () => {
                if (IS_PROD_BUILD) {
                    console.error('Failed to load Cogi CSS from CDN in production mode');
                    return;
                }
                console.warn('Failed to load primary Cogi CSS, falling back to CDN asset');
                const fallback = document.createElement('link');
                fallback.rel = 'stylesheet';
                fallback.href = COGI_CSS_URL;
                document.head.appendChild(fallback);
            };
            document.head.appendChild(link);
        }

        if (!scriptLoaded.current) {
            if (!window.Cogi) {
                const script = document.createElement('script');
                script.src = IS_PROD_BUILD ? COGI_JS_URL : COGI_LOCAL_JS_URL;
                script.onload = () => initWidget();
                script.onerror = () => {
                    if (IS_PROD_BUILD) {
                        console.error('Failed to load Cogi script from CDN in production mode');
                        return;
                    }
                    console.warn('Failed to load primary Cogi script, falling back to CDN asset');
                    const fallback = document.createElement('script');
                    fallback.src = COGI_JS_URL;
                    fallback.onload = () => initWidget();
                    fallback.onerror = (e) => console.error('Failed to load Cogi script fallback from CDN', e);
                    document.head.appendChild(fallback);
                };
                document.head.appendChild(script);
            } else {
                initWidget();
            }
            scriptLoaded.current = true;
        }

        const observer = new MutationObserver((mutations) => {
            for (const m of mutations) {
                if (m.attributeName === 'data-theme') {
                    const colors = getThemeColors();
                    const updated = applyThemeToLiveWidget(colors);
                    if (!updated) {
                        initWidget();
                    } else {
                        applyThemeWithRetries(colors);
                    }
                    break;
                }
            }
        });
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });

        return () => observer.disconnect();
    }, [applyThemeToLiveWidget, applyThemeWithRetries, initWidget]);

    const handleClose = () => {
        setView("closed");
        if (cogiInstance.current && cogiInstance.current.close) {
            try { cogiInstance.current.close(); } catch (e) { }
        }
    };

    return (
        <>
            <div
                className="chat-home-overlay"
                onClick={handleClose}
                style={{ display: view === "chat" ? "block" : "none" }}
            >
            </div>
        </>
    );
}

export default Chat;
